import {
  UpdateUserPreferencesV2Command,
  type UpdateUserPreferencesV2CommandInput,
  type UpdateUserPreferencesV2CommandOutput,
} from '@amzn/gbsa-orbit-typescript-client';
import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { apiOrbitClient } from '../index';
import { sharedUserPreferencesKeys, v2UserPreferencesMutationKeys } from './keys';

/**
 * v2 React Query mutation for updating user preferences.
 *
 * On success the user-preferences read cache is invalidated so any view
 * that depends on the active business unit refetches automatically. Other
 * BU-scoped queries should include the BU in their query key — when the
 * preferences refetch returns a new BU, those queries' keys change and
 * React Query refetches them on its own. No app-wide reload, no event bus.
 *
 * @param key - Identity of the preferences record. Used as the mutation
 *              key and to scope the success log line.
 * @param options - React Query overrides. `mutationKey` and `mutationFn`
 *                  are owned by the hook and cannot be supplied.
 */
export const useUpdateUserPreferencesMutation = (
  key: Pick<UpdateUserPreferencesV2CommandInput, 'programId' | 'userId'>,
  options?: Omit<
    UseMutationOptions<
      UpdateUserPreferencesV2CommandOutput,
      Error,
      UpdateUserPreferencesV2CommandInput
    >,
    'mutationKey' | 'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationKey: v2UserPreferencesMutationKeys.update(key),
    mutationFn: (data: UpdateUserPreferencesV2CommandInput) =>
      apiOrbitClient.send(new UpdateUserPreferencesV2Command(data)),
    // React Query v5: onSuccess is (data, variables, onMutateResult, context).
    // The 4-arg signature is correct here despite older docs showing 3.
    async onSuccess(data, variables, onMutateResult, context) {
      await queryClient.invalidateQueries({ queryKey: sharedUserPreferencesKeys.all });
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
